import React, { useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  Zap,
  Mail,
  Rocket,
  CheckCircle2,
  Phone,
  Lightbulb
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateGitexFlow } from "../api/api";

export default function GitexBoothExperience() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ prompt: '', email: '', phone: '' });
  const [isTyping, setIsTyping] = useState(false);

  const handleNext = () => {
    if (step < 3) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setStep(step + 1);
      }, 700);
    }
  };

  const handleSubmit = () => {
    setIsTyping(true);

    const request_data = { ...formData, phoneNo: `+971${formData.phone}` };
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    request_data.clientDate = today;

    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setStep(4); // Show success immediately
    }, 800);
    
    // Fire the API call without waiting - runs in parallel
    generateGitexFlow(request_data.prompt, request_data.clientDate, request_data.email, request_data.phoneNo ).catch(error => {
      console.error('Background API call failed:', error);
    });
  };

  const handleChange = (field, value) => {
    if (field === 'phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 9);
      setFormData((p) => ({ ...p, [field]: numericValue }));
    } else {
      setFormData((p) => ({ ...p, [field]: value }));
    }
  };

  const canProceed = () => {
    if (step === 0) return formData.prompt.trim().length > 10;
    if (step === 1) return formData.email.includes('@') && formData.email.includes('.');
    if (step === 2) return formData.phone.length === 9;
    return false;
  };

  const steps = [
    { id: 0, label: 'Idea', icon: <Lightbulb className="w-4 h-4" /> },
    { id: 1, label: 'Email', icon: <Mail className="w-4 h-4" /> },
    { id: 2, label: 'Phone', icon: <Phone className="w-4 h-4" /> },
    { id: 3, label: 'Generate', icon: <Rocket className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-indigo-50 to-white flex items-center justify-center p-6 relative overflow-hidden">
      {/* Soft animated floating shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{ y: [0, 30, 0], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-20 left-16 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [0, -30, 0], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 12, repeat: Infinity }}
          className="absolute bottom-32 right-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl"
        />
      </div>

      <div className="max-w-3xl w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            {/* Logo container */}
            <div className="flex items-center justify-cente">
              <img
                src="https://tuly.app/TulyGitex.png"
                alt="Tuly Logo"
                className="w-20 h-20 object-contain"
              />
            </div>

            <div className="text-left">
              <p className="text-blue-600 text-sm font-medium">AI-Powered Workflows</p>
            </div>
          </div>

          <div className="inline-block px-4 py-2 bg-blue-100/60 backdrop-blur-md rounded-full border border-blue-200 text-blue-700 text-sm font-medium">
            Gitex Global 2025 • Dubai
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative mb-8">

          <div className="flex justify-between items-center w-full relative">
            {steps.map((s, i) => (
              <div key={s.id} className="flex flex-col items-center text-xs font-medium text-slate-600">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    step >= s.id
                      ? 'bg-gradient-to-br from-yellow-500 to-orange-400 text-white shadow-md'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s.icon}
                </div>
                <span className={`mt-2 ${step >= s.id ? 'text-slate-800' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Line connector */}
          <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-200 -z-10" />
            <motion.div
              className="absolute top-4 left-0 h-0.5 bg-gradient-to-r from-yellow-500 to-orange-400 rounded-full -z-10"
              initial={{ width: '0%' }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>

          {/* Chat Section */}
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl border border-yellow-100 shadow-xl p-6 sm:p-10 min-h-[480px] flex flex-col space-y-6 transition-all">
            <AnimatePresence mode="wait">
              {step <= 4 && (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-6"
                >
                  {step === 0 && (
                    <>
                      <BotMessage
                        icon={<Zap className="w-5 h-5 text-white" />}
                        title="👋 Welcome to the Future!"
                        text="Let's design your custom AI workflow. What would you love to automate or improve?"
                      />
                      <UserInputArea
                        type="textarea"
                        placeholder="e.g., automate client follow-ups, summarize reports..."
                        value={formData.prompt}
                        onChange={(e) => handleChange('prompt', e.target.value)}
                        canProceed={canProceed()}
                        onNext={handleNext}
                      />
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <BotMessage
                        icon={<Mail className="w-5 h-5 text-white" />}
                        title="📩 Great! Where should we send it?"
                        text="Enter your email, and we’ll deliver your workflow instantly."
                      />
                      <UserInputArea
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        canProceed={canProceed()}
                        onNext={handleNext}
                      />
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <BotMessage
                        icon={<Phone className="w-5 h-5 text-white" />}
                        title="📱 Almost there!"
                        text="Please provide your phone number so we can send you a quick demo link."
                      />
                      <UserInputArea
                        type="phone"
                        prefix="+971"
                        placeholder="501234567"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        canProceed={canProceed()}
                        onNext={handleNext}
                      />
                    </>
                  )}

                  {step === 3 && (
                    <BotMessage
                      icon={<Rocket className="w-5 h-5 text-white" />}
                      title="🚀 Ready to experience the magic?"
                      text={
                        <>
                          Click below, and we’ll generate your AI workflow live and send it to your
                          inbox.
                          <button
                            onClick={handleSubmit}
                            className="mt-4 w-full bg-gradient-to-r from-blue-500 to-orange-400 text-white font-bold py-4 px-6 rounded-xl hover:from-blue-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                          >
                            <Rocket className="w-5 h-5" /> Generate My Workflow
                          </button>
                        </>
                      }
                    />
                  )}

                  {step === 4 && (
                    <BotMessage
                      icon={<CheckCircle2 className="w-6 h-6 text-white" />}
                      title="🎉 Your AI Workflow is on its way!"
                      text={
                        <p>
                          Check your inbox at{' '}
                          <span className="font-semibold text-blue-800">{formData.email}</span>. Didn’t
                          get it? Check spam or stop by our booth for instant support!
                        </p>
                      }
                      gradient="from-green-400 to-emerald-500"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-blue-700/70">
            <p>
              Crafted for innovators at{' '}
              <span className="font-semibold text-slate-900">Gitex Dubai 2025</span>
            </p>
          </div>
        </div>
      </div>
    
  );
}

function BotMessage({ icon, title, text, gradient = 'from-blue-500 to-orange-400' }) {
  return (
    <div className="flex gap-4 items-start animate-in">
      <div
        className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="bg-white rounded-2xl rounded-tl-sm p-5 shadow-md text-gray-800 border border-blue-100">
          <p className="font-semibold text-lg mb-2">{title}</p>
          <div className="text-gray-700 text-sm leading-relaxed">{text}</div>
        </div>
      </div>
    </div>
  );
}

function UserInputArea({ type, prefix, placeholder, value, onChange, canProceed, onNext }) {
  return (
    <div className="flex gap-4 justify-end animate-in">
      <div className="flex-1 max-w-xl">
        {type === 'textarea' ? (
          <textarea
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows="3"
            className="w-full px-5 py-4 rounded-2xl shadow-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
          />
        ) : (
          <div className="flex items-center bg-white rounded-2xl shadow-md overflow-hidden border border-blue-100">
            {type === 'phone' && (
              <span className="px-5 py-4 bg-gray-50 text-gray-700 font-semibold border-r">
                {prefix}
              </span>
            )}
            <input
              type={type}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              className="flex-1 px-5 py-4 focus:outline-none text-gray-800"
            />
          </div>
        )}
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`mt-3 px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ml-auto ${
            canProceed
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
